'use client';

import type { CSSProperties } from 'react';
import { useMember } from '@/hooks/publicSearch/member/useMember';
import { useDocumentImageWithSociety } from '@/hooks/publicSearch/useDocumentImageWithSociety';
import { findLogoDocument } from '@/publicSearch/utils/media';

/** Treat null, empty and 0 (i6's placeholder for a missing i4 key) as "no member". */
export function normalizeMemberId(
  id: number | string | null | undefined,
): string | null {
  if (id == null) return null;
  const str = String(id).trim();
  return str === '' || str === '0' ? null : str;
}

export type LotVendorLogoCandidates = {
  vendorsId?: number | string | null;
  currentOwnerId?: number | string | null;
};

/**
 * Resolves the selling member's logo for a sale lot (BLS-382), matching i4's
 * lot page: the vendor's logo when the vendor member has one, otherwise the
 * current owner's logo (never the breeder's). Many societies' lots carry no
 * vendor key at all (SA Brahman, Simbra, Simmentaler, Limousin...) and some
 * vendors have no logo document, so the owner fallback covers both. Returns a
 * displayable src via the document proxy, or null when neither has a logo.
 */
export function useLotVendorLogoSrc(
  candidates: LotVendorLogoCandidates,
  orgId: string | null | undefined,
): string | null {
  const org = orgId ?? '';
  // i6 emits 0 (not null) for a missing i4 vendor key.
  const vendorId = normalizeMemberId(candidates.vendorsId);
  const ownerId = normalizeMemberId(candidates.currentOwnerId);
  const { data: vendor } = useMember(vendorId ?? '', org);
  // Only fetch the owner when they might be needed (no vendor, or a different member).
  const ownerLookupId = ownerId && ownerId !== vendorId ? ownerId : '';
  const { data: owner } = useMember(ownerLookupId, org);
  const logoUrl =
    findLogoDocument(vendor?.member?.documents)?.url ??
    findLogoDocument(owner?.member?.documents)?.url ??
    null;
  const { imgSrc } = useDocumentImageWithSociety(logoUrl, org);
  return imgSrc ?? null;
}

type LotVendorLogoProps = {
  src: string;
  vendorName?: string | null;
  /** Max rendered height in px. */
  maxHeight?: number;
};

/** The vendor (stud) logo on a sale-lot animal page, as i4 sale catalogues show it. */
export function LotVendorLogo({
  src,
  vendorName,
  maxHeight = 56,
}: LotVendorLogoProps) {
  const style: CSSProperties = {
    display: 'block',
    maxHeight,
    maxWidth: 160,
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
  };

  return (
    // eslint-disable-next-line @next/next/no-img-element -- document-proxy blob URL with caller-supplied bounds; next/image needs static dimensions and a configured loader
    <img
      src={src}
      alt={vendorName ? `${vendorName} logo` : 'Vendor logo'}
      style={style}
    />
  );
}
